// HOW-SLOTS-WEB-AGENDA-TASKS (2026-07-09): agenda thunks. Every thunk resolves the CO's real dev
// backend id via unaliasId(coId) (the store/components everywhere else key by the FE's aliased
// int id -- see devBackendShim.js) right before the network call, so callers just pass the same
// id they already have (activeTeeup.id).
import actionTypes from "@actions/actionTypes"
import { unaliasId } from "@utils/devBackendShim"
import {
  listAgendaItems,
  createAgendaItem,
  updateAgendaItem,
  updateAgendaItemPeople,
  reorderAgendaItems,
  deleteAgendaItem,
  ConflictError,
} from "@utils/agendaTasksApi"

// Lazy, independent load -- callers fire this from a useEffect in the section component; it is
// NEVER awaited by the TeeUp detail shell (plan 4c "no hard dependency in the shell"). Fails soft
// to an empty list (agendaTasksApi.listAgendaItems never throws).
export const loadAgendaItems = coId => async dispatch => {
  if (!coId) return
  dispatch({ type: actionTypes.AGENDA_SET_LOADING, payload: { coId, loading: true } })
  const items = await listAgendaItems(unaliasId(coId))
  dispatch({ type: actionTypes.AGENDA_SET_ITEMS, payload: { coId, items } })
}

export const addAgendaItem = (coId, fields) => async dispatch => {
  if (!coId) return null
  const item = await createAgendaItem(unaliasId(coId), fields)
  if (item) dispatch({ type: actionTypes.AGENDA_ITEM_UPSERTED, payload: { coId, item } })
  return item
}

// onConflict(freshItems) is called (and the store refetched) on a 409 version_conflict, per the
// plan's "refetch + non-destructive notice" contract -- callers pass a toast/notice callback.
export const editAgendaItem =
  (coId, itemId, fields, expectedVersion, { onConflict } = {}) =>
  async dispatch => {
    if (!coId || !itemId) return null
    try {
      const item = await updateAgendaItem(itemId, fields, expectedVersion)
      if (item) dispatch({ type: actionTypes.AGENDA_ITEM_UPSERTED, payload: { coId, item } })
      return item
    } catch (e) {
      if (e instanceof ConflictError) {
        const items = await listAgendaItems(unaliasId(coId))
        dispatch({ type: actionTypes.AGENDA_SET_ITEMS, payload: { coId, items } })
        if (onConflict) onConflict(items)
        return null
      }
      throw e
    }
  }

// WU-P2-FE-a (2026-07-09) -- people-picker thunk. fields = {owner_participant_id?, involved_people?}
// (owner_participant_id is a participants.id uuid, or explicit null to clear; involved_people is
// the FULL new array, or [] to clear). Same 409 version_conflict -> refetch + onConflict notice
// convention as editAgendaItem above.
export const setAgendaItemPeople =
  (coId, itemId, fields, expectedVersion, { onConflict } = {}) =>
  async dispatch => {
    if (!coId || !itemId) return null
    try {
      const item = await updateAgendaItemPeople(unaliasId(coId), itemId, fields, expectedVersion)
      if (item) dispatch({ type: actionTypes.AGENDA_ITEM_UPSERTED, payload: { coId, item } })
      return item
    } catch (e) {
      if (e instanceof ConflictError) {
        const items = await listAgendaItems(unaliasId(coId))
        dispatch({ type: actionTypes.AGENDA_SET_ITEMS, payload: { coId, items } })
        if (onConflict) onConflict(items)
        return null
      }
      throw e
    }
  }

export const markAgendaItemCovered = (coId, item) =>
  editAgendaItem(coId, item.id, { discussed_at: new Date().toISOString() }, item.version)

export const reorderAgenda = (coId, itemIds) => async dispatch => {
  if (!coId) return false
  // optimistic local reorder; loadAgendaItems below re-syncs from the server (position is
  // authoritative there) so a failed reorder self-heals on the next read.
  const ok = await reorderAgendaItems(unaliasId(coId), itemIds)
  const items = await listAgendaItems(unaliasId(coId))
  dispatch({ type: actionTypes.AGENDA_REORDERED, payload: { coId, items } })
  return ok
}

export const removeAgendaItem = (coId, itemId) => async dispatch => {
  if (!coId || !itemId) return false
  const ok = await deleteAgendaItem(itemId)
  if (ok) dispatch({ type: actionTypes.AGENDA_ITEM_REMOVED, payload: { coId, itemId } })
  return ok
}
